import React, { useState, useEffect } from 'react';
import { FaFolder, FaFilePdf, FaFileWord, FaFileExcel, FaFilePowerpoint, FaFileImage, FaFileAlt, FaFileArchive, FaFile, FaChevronLeft, FaSearch, FaLock, FaDownload } from 'react-icons/fa';
import './Normativa.css';
import { getApiUrl } from '../../config/api';

const Normativa = () => {
    const [structure, setStructure] = useState([]);
    const [currentPath, setCurrentPath] = useState([]); // Array of folder names
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const fetchNormativas = async () => {
            try {
                const token = localStorage.getItem('authToken') || (localStorage.getItem('auth') ? JSON.parse(localStorage.getItem('auth')).authToken : '');
                const response = await fetch(getApiUrl('/normativas'), {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                if (response.ok) {
                    const data = await response.json();
                    setStructure(data);
                }
            } catch (error) {
                console.error("Error fetching normativas:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchNormativas();
    }, []);

    // Helper to get current level of structure based on currentPath
    const getCurrentLevel = () => {
        let level = structure;
        for (const pathPart of currentPath) {
            const folder = level.find(item => item.name === pathPart);
            if (folder && folder.children) {
                level = folder.children;
            } else {
                return [];
            }
        }
        return level;
    };

    const navigateToFolder = (item) => {
        // Si el item es un string (desde el breadcrumb) lo navegamos directo
        if (typeof item === 'string') {
            setCurrentPath([...currentPath, item]);
            return;
        }

        // Si es un objeto item (clic en la grid)
        if (item.metadata?.is_protected) {
            setPasswordModal({ open: true, file: item, type: 'directory' });
            setPasswordInput('');
            setUnlockError('');
        } else {
            setCurrentPath([...currentPath, item.name]);
        }
    };

    const goBack = () => {
        setCurrentPath(currentPath.slice(0, -1));
    };

    const currentLevel = getCurrentLevel();

    // Filtered items based on search
    const filteredItems = currentLevel.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // State for password modal
    const [passwordModal, setPasswordModal] = useState({ open: false, file: null, type: null });
    const [passwordInput, setPasswordInput] = useState('');
    const [unlockError, setUnlockError] = useState('');
    const [unlockedFolders, setUnlockedFolders] = useState({}); // Map path -> password

    const handleFileClick = (e, item) => {
        if (item.metadata && item.metadata.is_protected) {
            if (e) e.preventDefault();
            setPasswordModal({ open: true, file: item, type: 'file' });
            setPasswordInput('');
            setUnlockError('');
            return true; // Indicamos que estaba protegido
        }
        return false;
    };

    const handleUnlock = async (e) => {
        e.preventDefault();
        setUnlockError('');

        try {
            const response = await fetch(getApiUrl('/files/token'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ path: passwordModal.file.path, password: passwordInput })
            });
            const data = await response.json();

            if (data.success) {
                if (passwordModal.type === 'directory') {
                    // Guardar contraseña para esta carpeta
                    setUnlockedFolders(prev => ({
                        ...prev,
                        [passwordModal.file.path]: passwordInput
                    }));
                    setCurrentPath([...currentPath, passwordModal.file.name]);
                    setPasswordModal({ open: false, file: null, type: null });
                } else {
                    // Success - trigger download
                    // Ahora usamos TOKEN si viene en la respuesta, si no fallback
                    const downloadUrl = data.token
                        ? `${passwordModal.file.url}&token=${data.token}`
                        : `${passwordModal.file.url}&password=${encodeURIComponent(passwordInput)}`;

                    window.location.href = downloadUrl;
                    setPasswordModal({ open: false, file: null, type: null });
                }
            } else {
                setUnlockError(data.message || "Contraseña incorrecta");
            }
        } catch (err) {
            setUnlockError("Error al verificar contraseña");
        }
    };



    const getFileIcon = (fileName) => {
        const ext = fileName.split('.').pop().toLowerCase();
        switch (ext) {
            case 'pdf': return <FaFilePdf />;
            case 'doc': case 'docx': return <FaFileWord />;
            case 'xls': case 'xlsx': return <FaFileExcel />;
            case 'ppt': case 'pptx': return <FaFilePowerpoint />;
            case 'jpg': case 'jpeg': case 'png': case 'gif': return <FaFileImage />;
            case 'txt': return <FaFileAlt />;
            case 'zip': case 'rar': case '7z': return <FaFileArchive />;
            default: return <FaFile />;
        }
    };

    const getInheritedPassword = (item) => {
        // Busca si alguna carpeta padre de este item tiene contraseña guardada
        if (!item.path) return null;
        // Check exact match (if item is directory)
        if (unlockedFolders[item.path]) return unlockedFolders[item.path];

        // Check parents
        const parts = item.path.split('/');
        // Iterate from deep to shallow
        for (let i = parts.length - 1; i >= 0; i--) {
            const pathToCheck = parts.slice(0, i).join('/');
            if (unlockedFolders[pathToCheck]) return unlockedFolders[pathToCheck];
        }
        return null;
    };

    const handleDownloadClick = async (e, item) => {
        e.preventDefault();

        // 1. Check if explicitly protected (requires modal)
        if (item.metadata && item.metadata.is_protected) {
            setPasswordModal({ open: true, file: item, type: 'file' });
            setPasswordInput('');
            setUnlockError('');
            return;
        }

        // 2. Check for inherited password (or get "" for public)
        const inheritedPass = getInheritedPassword(item) || "";

        // Request Token ALWAYS (even for public files, so we pass Auth Context for RBAC)
        const myToken = localStorage.getItem('authToken') || (localStorage.getItem('auth') ? JSON.parse(localStorage.getItem('auth')).authToken : '');

        try {
            const response = await fetch(getApiUrl('/files/token'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${myToken}`
                },
                body: JSON.stringify({ path: item.path, password: inheritedPass })
            });
            const data = await response.json();

            if (data.success && data.token) {
                // Success: Download with token
                window.open(`${item.url}&token=${data.token}`, '_blank');
            } else {
                // Error: Access Denied or Password Required
                // If specific role error, show alert
                if (data.message === "Acceso denegado: Rol insuficiente") {
                    alert("No tienes permisos para descargar este archivo.");
                } else {
                    // Fallback for password error -> triggers password modal if we re-click? 
                    // Or assume it's public link fallback (which will fail if restricted)
                    console.error("Download token denied:", data.message);
                    // If we have password but failed? Maybe wrong pass?
                    // If we had no pass ("") and failed, it means restricted.
                    if (!data.success && data.message) alert(data.message);
                }
            }
        } catch (err) {
            console.error("Network error requesting token:", err);
            // Fallback (will likely fail 403 if restricted)
            window.open(item.url, '_blank');
        }
    };

    return (
        <div className="normativa-page">
            <div className="normativa-page-glow-top"></div>
            <div className="normativa-page-glow-bottom"></div>
            <div className="normativa-container" data-aos="fade-up">
                <header className="normativa-header">
                    <div className="header-top">
                        <h1 className="normativa-title">Normativas del Servidor</h1>
                    </div>
                    <div className="header-controls">
                        <div className="breadcrumb">
                            <span className="breadcrumb-item" onClick={() => setCurrentPath([])}>Inicio</span>
                            {currentPath.map((part, index) => (
                                <span key={index} className="breadcrumb-item">
                                    <span className="separator">/</span>
                                    <span onClick={() => setCurrentPath(currentPath.slice(0, index + 1))}>{part}</span>
                                </span>
                            ))}
                        </div>
                        <div className="search-box">
                            <FaSearch className="search-icon" />
                            <input
                                type="text"
                                placeholder="Buscar normativa..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                </header>

                <div className="normativa-content">
                    {currentPath.length > 0 && (
                        <button className="back-button" onClick={goBack}>
                            <FaChevronLeft /> Volver atrás
                        </button>
                    )}

                    {loading ? (
                        <div className="normativa-loading">Cargando normativas...</div>
                    ) : (
                        <div className="normativa-grid">
                            {filteredItems.length === 0 ? (
                                <div className="no-items">No se encontraron archivos o carpetas.</div>
                            ) : (
                                filteredItems.map((item, index) => (
                                    <div
                                        key={index}
                                        className={`normativa-item ${item.type}`}
                                        data-aos="zoom-in"
                                        data-aos-delay={index * 50}
                                        onClick={(e) => {
                                            if (item.type === 'directory') {
                                                navigateToFolder(item);
                                            } else {
                                                handleDownloadClick(e, item);
                                            }
                                        }}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <div className="item-icon">
                                            {item.type === 'directory' ? <FaFolder className="folder-icon" /> : <span className="custom-file-icon">{getFileIcon(item.name)}</span>}
                                        </div>
                                        <div className="item-info">
                                            <span className="item-name">
                                                {item.name}
                                                {item.metadata?.is_protected ? <FaLock style={{ marginLeft: '8px', fontSize: '0.8em', color: '#e74c3c' }} title="Protegido" /> : null}
                                            </span>
                                            {item.type === 'file' ? <span className="item-size">{(item.size / 1024).toFixed(2)} KB</span> : null}
                                        </div>
                                        <div className="item-actions">
                                            {item.type === 'directory' ? (
                                                <button className="view-btn" onClick={() => navigateToFolder(item)}>
                                                    Abrir Carpeta
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={(e) => handleDownloadClick(e, item)}
                                                    className="download-btn"
                                                >
                                                    <FaDownload /> {item.metadata?.is_protected ? 'Desbloquear' : 'Descargar'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>

                {/* Password Modal */}
                {passwordModal.open && (
                    <div className="normativa-modal-overlay">
                        <div className="normativa-modal">
                            <h3>Archivo Protegido</h3>
                            <p>Introduce la contraseña para descargar <strong>{passwordModal.file.name}</strong></p>
                            <form onSubmit={handleUnlock}>
                                <input
                                    type="password"
                                    placeholder="Contraseña"
                                    value={passwordInput}
                                    onChange={e => setPasswordInput(e.target.value)}
                                    autoFocus
                                />
                                {unlockError && <div className="error-msg">{unlockError}</div>}
                                <div className="modal-actions">
                                    <button type="button" onClick={() => setPasswordModal({ open: false, file: null })}>Cancelar</button>
                                    <button type="submit" className="btn-primary">Desbloquear</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div >
    );
};

export default Normativa;
